'use client';
import BreadcrumbBase, {
  type TextColorThemeUnion,
} from '@mnd-frontend/ui/public/components/Breadcrumb';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import type {
  BgColorVariants,
  TextColorDataType,
  TextColorVariants,
} from '@mnd-frontend/ui/public/types/color';
import { camelCase } from 'lodash-es';
import styled from 'styled-components';
import type { PageFragment } from '../wp-generated/types';

const BREADCRUMB_TEXT_THEME = {
  LIGHT: 'light',
  DARK: 'dark',
} satisfies { [key in Uppercase<TextColorThemeUnion>]: TextColorThemeUnion };

const colorMap = {
  'gradient-2': BREADCRUMB_TEXT_THEME.LIGHT,
  'gradient-3': BREADCRUMB_TEXT_THEME.LIGHT,
  gray: BREADCRUMB_TEXT_THEME.LIGHT,
  orange: BREADCRUMB_TEXT_THEME.DARK,
  pink: BREADCRUMB_TEXT_THEME.DARK,
  purple: BREADCRUMB_TEXT_THEME.LIGHT,
  'deep-purple': BREADCRUMB_TEXT_THEME.LIGHT,
  red: BREADCRUMB_TEXT_THEME.LIGHT,
  transparent: BREADCRUMB_TEXT_THEME.DARK,
  white: BREADCRUMB_TEXT_THEME.DARK,
} satisfies { [key in BgColorVariants]: TextColorThemeUnion };

type colorKeyType = keyof typeof colorMap;
type HeroObjectKeyWithBgColor = 'hero' | 'heroBg' | 'heroCenter' | 'heroForm' | 'heroTrial';
type HeroObjectkeyWithTextColor = Extract<
  HeroObjectKeyWithBgColor,
  'hero' | 'heroForm' | 'heroTrial'
>;

const transformStringArrayToString = (
  value: Array<colorKeyType> | colorKeyType | unknown,
): colorKeyType | null => {
  let colorValueCopy = value;

  if (Array.isArray(value)) {
    colorValueCopy = value[0];
  }

  if (typeof colorValueCopy === 'string' && Object.keys(colorMap).includes(colorValueCopy)) {
    return colorValueCopy as colorKeyType;
  }

  return null;
};

const getHeroBackgroundColor = (hero: PageFragment['hero']) => {
  const templateName = hero?.heroTemplate?.[0];

  if (templateName === 'hero-ai') {
    return ['image'];
  }

  const keyName = templateName ? (camelCase(templateName) as HeroObjectKeyWithBgColor) : null;
  return keyName ? hero?.[keyName]?.bgColor : null;
};

const mapTextColorToTheme = (userSettingColor: TextColorVariants | undefined | null) => {
  if (!userSettingColor) return null;

  return userSettingColor === 'white' ? BREADCRUMB_TEXT_THEME.LIGHT : BREADCRUMB_TEXT_THEME.DARK;
};

const mapBgColorToTextColorTheme = (color: unknown): TextColorThemeUnion => {
  const colorKey = transformStringArrayToString(color) ?? 'transparent';
  return colorMap[colorKey];
};

const BreadcrumbsContainer = styled(ContentMaxWidth)`
  padding-top: var(--header-height);
`;

// #region Component
const BreadcrumbsComponent = ({
  breadcrumbs,
  hero,
  background = null,
}: {
  breadcrumbs: {
    url: string;
    text: string;
  }[];
} & (
  | {
      hero?: PageFragment['hero'];
      background?: undefined;
    }
  | { background: string | null; hero?: undefined }
)) => {
  const heroBackgroundColor = (hero && getHeroBackgroundColor(hero)) || [background] || [
      'transparent',
    ];

  /**
   * Text color in the old design is set based on bg-color, while it is done
   * by user setting in new design 2025.
   */
  const getTextColorTheme = () => {
    const keyName = camelCase(hero?.heroTemplate?.[0] ?? undefined) as HeroObjectKeyWithBgColor;
    let customColor: ReturnType<typeof mapTextColorToTheme> | null = null;

    if (['hero', 'heroForm', 'heroTrial'].includes(keyName) && hero?.[keyName]?.use2025Design) {
      customColor = mapTextColorToTheme(
        hero?.[keyName as HeroObjectkeyWithTextColor]?.textColor?.at(0) as TextColorDataType,
      );
    }

    return customColor || mapBgColorToTextColorTheme(heroBackgroundColor);
  };

  return (
    <BreadcrumbsContainer>
      <BreadcrumbBase textColorTheme={getTextColorTheme()} breadcrumb={breadcrumbs} />
    </BreadcrumbsContainer>
  );
};
// #endregion
export default BreadcrumbsComponent;
